import {
  fetchLucaWorkflowTools,
  ILucaWorkflowTool,
  IWorkflowEventTriggerTypeOption,
  IWorkflowFilterTypeOption,
  lucaWorkflowToolsQueryKey,
  LUCA_WORKFLOW_TOOLS_STALE_MS,
  useWidgetSession,
  useWorkflowEventTriggerTypes,
  useWorkflowFilterTypes
} from '@query'
import { IUsershipTag } from '@root/query/constants'
import { useUsershipTags } from '@root/query/hooks'
import { useBusinessContext } from '@root/context'
import { useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useMemo } from 'react'

export interface WorkflowCatalogsContextValue {
  eventTriggerTypes: IWorkflowEventTriggerTypeOption[]
  filterCatalog: IWorkflowFilterTypeOption[]
  usershipTags: IUsershipTag[]
  lucaTools: ILucaWorkflowTool[]
  lucaToolTitles: Record<string, string>
  filterQuery: {
    isLoading: boolean
    isError: boolean
    refetch: () => void
  }
  lucaToolsQuery: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    refetch: () => void
  }
}

const WorkflowCatalogsContext = createContext<WorkflowCatalogsContextValue>({
  eventTriggerTypes: [],
  filterCatalog: [],
  usershipTags: [],
  lucaTools: [],
  lucaToolTitles: {},
  filterQuery: {
    isLoading: false,
    isError: false,
    refetch: () => undefined
  },
  lucaToolsQuery: {
    isLoading: false,
    isError: false,
    isSuccess: false,
    refetch: () => undefined
  }
})

export const WorkflowCatalogsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug } = useBusinessContext()
  const { data: eventTriggerTypes = [] } = useWorkflowEventTriggerTypes()
  const { data: usershipTagCatalog } = useUsershipTags()
  const {
    data: filterCatalog = [],
    isLoading: isFilterCatalogLoading,
    isError: isFilterCatalogError,
    refetch: refetchFilterCatalog
  } = useWorkflowFilterTypes()

  const {
    data: widgetSession,
    isLoading: sessionLoading,
    refetch: refetchWidgetSession
  } = useWidgetSession(Boolean(slug), slug)
  const sessionToken = widgetSession?.token ?? null

  const toolsQuery = useQuery({
    queryKey: lucaWorkflowToolsQueryKey(slug),
    queryFn: () => {
      if (!slug || !sessionToken) {
        throw new Error('missing Luca session')
      }
      return fetchLucaWorkflowTools(
        { token: sessionToken, country: widgetSession?.country ?? 'FI' },
        slug
      )
    },
    enabled: Boolean(slug && sessionToken),
    staleTime: LUCA_WORKFLOW_TOOLS_STALE_MS,
    gcTime: LUCA_WORKFLOW_TOOLS_STALE_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  const lucaTools = toolsQuery.data?.tools ?? []
  const lucaToolTitles = useMemo(
    () => Object.fromEntries(lucaTools.map(tool => [tool.name, tool.title || tool.name])),
    [lucaTools]
  )

  const showToolsError = toolsQuery.isError || (!sessionLoading && !sessionToken)
  const showToolsLoading =
    !showToolsError && (sessionLoading || toolsQuery.isFetching || toolsQuery.isPending)

  const value: WorkflowCatalogsContextValue = {
    eventTriggerTypes,
    filterCatalog,
    usershipTags: usershipTagCatalog?.roles ?? [],
    lucaTools,
    lucaToolTitles,
    filterQuery: {
      isLoading: isFilterCatalogLoading,
      isError: isFilterCatalogError,
      refetch: () => {
        void refetchFilterCatalog()
      }
    },
    lucaToolsQuery: {
      isLoading: showToolsLoading,
      isError: showToolsError,
      isSuccess: toolsQuery.isSuccess,
      refetch: () => {
        if (sessionToken) void toolsQuery.refetch()
        else void refetchWidgetSession()
      }
    }
  }

  return (
    <WorkflowCatalogsContext.Provider value={value}>{children}</WorkflowCatalogsContext.Provider>
  )
}

export const useWorkflowCatalogs = (): WorkflowCatalogsContextValue =>
  useContext(WorkflowCatalogsContext)
